import { FC, useRef, useCallback, useState, useEffect } from "react";
import { TableHead } from "@/components/Base/Table";
import {
  SortableTableHeader,
  SortableTableHeaderProps,
} from "./SortableTableHeader";

interface ResizableTableHeaderProps extends SortableTableHeaderProps {
  onWidthChange: (columnId: string, width: number) => void;
}

export const ResizableTableHeader: FC<ResizableTableHeaderProps> = ({
  column,
  sortCriteria,
  onSort,
  onWidthChange,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const columnRef = useRef<HTMLTableCellElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Sync with column.width changes when not resizing
  useEffect(() => {
    if (!isResizing && columnRef.current) {
      columnRef.current.style.width = `${column.width}px`;
    }
  }, [column.width, isResizing]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!columnRef.current) return;

      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = column.width;

      const handleMouseMove = (e: MouseEvent) => {
        if (!columnRef.current) return;

        const deltaX = e.clientX - startXRef.current;
        const newWidth = Math.max(
          startWidthRef.current + deltaX,
          column.minWidth
        );

        const constrainedWidth = column.maxWidth
          ? Math.min(newWidth, column.maxWidth)
          : newWidth;

        // Apply width directly to DOM for immediate visual feedback
        columnRef.current.style.width = `${constrainedWidth}px`;
      };

      const handleMouseUp = () => {
        if (!columnRef.current) return;

        setIsResizing(false);

        // Calculate final width from the final mouse position
        const finalDeltaX = startXRef.current
          ? (document.querySelector(":hover") as unknown as MouseEvent)
              ?.clientX - startXRef.current
          : 0;
        const finalWidth = Math.max(
          startWidthRef.current + (finalDeltaX || 0),
          column.minWidth
        );

        const constrainedFinalWidth = column.maxWidth
          ? Math.min(finalWidth, column.maxWidth)
          : finalWidth;

        // Get the actual width from the DOM element which should be accurate
        const actualWidth =
          parseFloat(columnRef.current.style.width) || constrainedFinalWidth;

        onWidthChange(column.id, actualWidth);

        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleMouseUp);
    },
    [column.id, column.width, column.minWidth, column.maxWidth, onWidthChange]
  );

  return (
    <TableHead
      ref={columnRef}
      className={`relative select-none ${column.className || ""}`}
      style={{
        width: `${column.width}px`,
        minWidth: `${column.minWidth}px`,
        maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
        textAlign: column.align,
        boxSizing: "border-box",
        overflow: "hidden",
        // Prevent text selection during resize
        userSelect: isResizing ? "none" : undefined,
      }}
    >
      {/* Sortable Header Content */}
      <SortableTableHeader
        column={column}
        sortCriteria={sortCriteria}
        onSort={onSort}
      />

      {/* Resize Handle */}
      <div
        className={`absolute top-0 h-full w-2 cursor-col-resize hover:bg-primary/50 ${
          isResizing ? "bg-primary" : ""
        }`}
        onMouseDown={handleMouseDown}
        style={{
          right: "-1px",
        }}
      >
        {/* Visual indicator for resize handle */}
        <div className="h-full w-full bg-primary/20 opacity-0 transition-opacity hover:opacity-100" />
      </div>
    </TableHead>
  );
};
